import { Button } from '@/components/shadcn/button'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useNavigate, useLocation } from 'react-router'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import type { MdDocumentModel } from './mdDocumentModel'
import { getSectionById } from './mdDocumentModel'
import { MdTextDocumentHeading } from './MdTextMetadataSummary'
import { MdTextOutline } from './MdTextOutline'
import { downloadMarkdownFile } from './mdTextDownload'
import type { MdTextResponse } from './types'
import { buildMdTextViewerPath, withSectionSearchParam, type MdTextViewerLocationState } from './mdTextViewerNavigation'
import { MdTextSearchNavigator } from './MdTextSearchNavigator'
import { SearchInput } from '../shared/SearchInput'
import { useMdTextSearchIndex } from './useMdTextSearchIndex'
import { useCallback, useState } from 'react'
import { getDefaultSectionId } from './mdDocumentModel'
import { IconOpenInNewOutline } from '@icons/IconOpenInNewOutline'
import { IconDownloadOutline } from '@icons/IconDownloadOutline'

export function MdTextLauncher({
  contentId,
  title,
  description,
  documentModel,
  sheetPayload,
  cachedResponse,
  searchSlot,
  actionsSlot,
  showTitleAndDescription = false,
}: {
  contentId?: string
  title?: string
  description?: string
  showTitleAndDescription?: boolean
  documentModel: MdDocumentModel
  sheetPayload?: Record<string, unknown>
  cachedResponse?: MdTextResponse
  searchSlot?: React.ReactNode
  actionsSlot?: React.ReactNode
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { appKey } = useAppContext()
  const { tileFilterSelections } = useTileFilters()
  const [searchValue, setSearchValue] = useState('')
  const [selectedSectionId, setSelectedSectionId] = useState(() => getDefaultSectionId(documentModel.sections))

  const openViewer = useCallback(
    (sectionId?: string, startInEditMode?: boolean) => {
      if (!appKey || !contentId) {
        return
      }
      const pathname = buildMdTextViewerPath(appKey, contentId)
      const search = withSectionSearchParam(location.search, sectionId)
      const state: MdTextViewerLocationState = {
        from: `${location.pathname}${location.search}`,
        payload: sheetPayload,
        tileFilterSelections,
        startInEditMode,
        cachedResponse,
      }
      navigate({ pathname, search }, { state })
    },
    [
      appKey,
      cachedResponse,
      contentId,
      location.pathname,
      location.search,
      navigate,
      sheetPayload,
      tileFilterSelections,
    ]
  )

  const {
    hasSearchTerm,
    matchCount,
    activeMatchIndex,
    goToPreviousMatch,
    goToNextMatch,
    activeMatch,
    isSearchPending,
  } = useMdTextSearchIndex({
    searchValue,
    isEditing: false,
    content: documentModel.source,
    sections: documentModel.sections,
  })

  const handleOpenDocument = () => {
    openViewer(activeMatch?.sectionId ?? selectedSectionId)
  }

  const handleOutlineSelect = (sectionId: string) => {
    setSelectedSectionId(sectionId)
    if (contentId) {
      openViewer(sectionId)
    }
  }

  const handleDownload = () => {
    downloadMarkdownFile(title, documentModel.source)
  }

  const highlightedSectionId = activeMatch?.sectionId ?? selectedSectionId
  const highlightedSection = getSectionById(documentModel.sections, highlightedSectionId ?? '')

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <MdTextDocumentHeading
            title={title}
            description={description}
            summary={documentModel.summary}
            showTitleAndDescription={showTitleAndDescription}
          />
          <p className="text-xs text-muted-foreground">
            This document is too large to preview here. Use the outline to jump to a section in the reader, or open the
            full document. PDF export is unavailable for large documents because the full document is not mounted.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {searchSlot ?? <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search" />}
          {actionsSlot}
          <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
            <IconDownloadOutline size={14} className="mr-2" />
            Download
          </Button>
          <Button type="button" size="sm" onClick={handleOpenDocument} disabled={!contentId}>
            <IconOpenInNewOutline size={14} className="mr-2" />
            Open document
          </Button>
        </div>
      </div>
      <MdTextSearchNavigator
        hasSearchTerm={hasSearchTerm || isSearchPending}
        matchCount={matchCount}
        activeMatchIndex={activeMatchIndex}
        onPrevious={goToPreviousMatch}
        onNext={goToNextMatch}
        className="relative right-auto top-auto"
      />
      {hasSearchTerm && matchCount > 0 && (
        <p className="text-xs text-muted-foreground">
          Use the arrows to step through matches, then open the document to read the highlighted section.
        </p>
      )}
      <ScrollArea className="min-h-0 flex-1 rounded-md border p-3">
        <MdTextOutline
          sections={documentModel.sections}
          onSelectSection={handleOutlineSelect}
          selectedSectionId={highlightedSectionId}
        />
      </ScrollArea>
      {highlightedSection && (
        <p className="text-sm text-muted-foreground">
          Selected: <span className="font-medium text-foreground">{highlightedSection.title}</span>
        </p>
      )}
    </div>
  )
}
